import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { IImpersonation } from 'src/app/shared/interfaces/IImpersonation';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { validateDateChronology } from 'src/app/shared/validators/date-chronology.validator';
import { IEmployeeSCDItem } from 'src/app/shared/interfaces/IEmployeeSCDItem';
import { DatepickerHeaderComponent } from '../../datepicker-header/datepicker-header.component';
import { PeoplePickerComponent } from '../../people-picker/people-picker.component';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';

import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { TranslatePipe } from '@ngx-translate/core';
import { DateService } from '../../../services/date.service';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-assign-impersonator-dialog',
  templateUrl: './assign-impersonator-dialog.component.html',
  styleUrls: ['./assign-impersonator-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    PeoplePickerComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatDialogActions,
    TranslatePipe,
    MatError,
  ],
})
export class AssignImpersonatorComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<AssignImpersonatorComponent>>(MatDialogRef);
  private dateService = inject(DateService);
  private destroy$: Subject<boolean> = new Subject<boolean>();

  public assignImpersonatorForm: FormGroup;
  public selectedEmployee: IEmployeeSCDItem;
  public DatepickerHeaderComponent = DatepickerHeaderComponent;

  protected maxValidToDate: Date | null = null;

  ngOnInit(): void {
    this.assignImpersonatorForm = new FormGroup(
      {
        validFrom: new FormControl<Date>(new Date(), [Validators.required]),
        validTo: new FormControl<Date>(new Date(), [Validators.required]),
      },
      {
        validators: [validateDateChronology('validFrom', 'validTo'), this.validateMaxOneYear('validFrom', 'validTo')],
      },
    );

    this.updateMaxValidToDate();

    this.subscribeToValidFromChanges();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private updateMaxValidToDate(): void {
    const validFromDate = this.assignImpersonatorForm.get('validFrom')?.value;
    if (validFromDate) {
      this.maxValidToDate = new Date(validFromDate);
      this.maxValidToDate.setFullYear(this.maxValidToDate.getFullYear() + 1);
    } else {
      this.maxValidToDate = new Date();
      this.maxValidToDate.setFullYear(this.maxValidToDate.getFullYear() + 1);
    }
  }

  private subscribeToValidFromChanges(): void {
    this.assignImpersonatorForm
      .get('validFrom')
      ?.valueChanges.pipe(
        tap((validFromDate: Date) => {
          if (validFromDate) {
            this.changeValidTo();
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private changeValidTo(): void {
    this.updateMaxValidToDate();

    const validToControl = this.assignImpersonatorForm.get('validTo');
    if (validToControl?.value && this.maxValidToDate && validToControl.value > this.maxValidToDate) {
      validToControl.setValue(this.maxValidToDate);
    }
  }

  private validateMaxOneYear(fromControlName: string, toControlName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const fromControl = control.get(fromControlName);
      const toControl = control.get(toControlName);

      if (!fromControl?.value || !toControl?.value) {
        return null;
      }

      const fromDate = new Date(fromControl.value);
      const toDate = new Date(toControl.value);

      const oneYearLater = new Date(fromDate);
      oneYearLater.setFullYear(fromDate.getFullYear() + 1);

      return toDate > oneYearLater ? { maxOneYear: true } : null;
    };
  }

  public closeWindow(): void {
    this.dialogRef.close();
  }

  public save(): void {
    const impersonation: IImpersonation = {
      parentGid: this.selectedEmployee.scdGid,
      parentName: this.selectedEmployee.scdName,
      validFrom: this.dateService.toIsoStringWithoutTimeZone(new Date(this.assignImpersonatorForm.get('validFrom').getRawValue())),
      validTo: this.dateService.toIsoStringWithoutTimeZone(new Date(this.assignImpersonatorForm.get('validTo').getRawValue())),
    };

    this.dialogRef.close(impersonation);
  }

  public setSelectedEmployee(employee: IEmployeeSCDItem[]): void {
    this.selectedEmployee = employee[0];
  }
}
